import {
    MUTATE_LIST_CREATE,
    MUTATE_MAP_CREATE,
    MUTATE_MIXED_CREATE,
    MUTATE_OPERATION,
    MUTATE_OPERATION_ENHANCED,
    MUTATE_CLEAR,
} from "constants/action-types";
import { Map, List, fromJS } from "immutable";

export enum OperationType {
    Set = "set",
    Remove = "remove",
    Insert = "insert",
    Move = "move",
    Clear = "clear",
}

export interface IMutationRef {
    namespace: string;
    document_id: string;
    object_id: string;
    branch: string;
    revision: number;
    type: MutableType;
}

export interface IOperation {
    // Key to reference mutable mix's inner property
    childKey?: string;
    key?: string;
    operation: OperationType;
    value?: any;
    // - LIST's zero-index
    // - LIST origin index for move operation
    index?: number;
    destination_index?: number; // For LIST move
    deltas?: List<any>;
    rowId?: string;
}

export enum MutableType {
    Map = "MAP",
    List = "LIST",
    Mixed = "MIXED",
}

export interface IOperationGroup {
    mutableType: MutableType;
    objectId: string;
    operations: IOperation[];
}

// MAP OPERATIONS
export const mapSetOperation = (objectId, key, value, meta = {}) =>
    mutateOperation(
        MutableType.Map,
        objectId,
        [
            {
                key,
                operation: OperationType.Set,
                value,
            },
        ],
        meta,
    );

export const mapRemoveOperation = (objectId, key, meta = {}) =>
    mutateOperation(
        MutableType.Map,
        objectId,
        [
            {
                key,
                operation: OperationType.Remove,
            },
        ],
        meta,
    );

// LIST OPERATIONS
export const listInsertOperation = (objectId: string, value, index?: number, meta = {}) =>
    mutateOperation(
        MutableType.List,
        objectId,
        [
            {
                index,
                operation: OperationType.Insert,
                value: Map.isMap(value) ? value : fromJS(value),
            },
        ],
        meta,
    );

export const listRemoveOperation = (objectId: string, index: number, meta = {}) =>
    mutateOperation(
        MutableType.List,
        objectId,
        [
            {
                index,
                operation: OperationType.Remove,
            },
        ],
        meta,
    );

export const listMoveOperation = (objectId: string, index: number, destinationIndex: number, meta = {}) =>
    mutateOperation(
        MutableType.List,
        objectId,
        [
            {
                destination_index: destinationIndex,
                index,
                operation: OperationType.Move,
            },
        ],
        meta,
    );

export const listSetOperation = (objectId: string, index: number, value, deltas?, meta = {}) =>
    mutateOperation(
        MutableType.List,
        objectId,
        [
            {
                index,
                operation: OperationType.Set,
                value,
                deltas,
            },
        ],
        meta,
    );

// MIXED OPERATIONS
/**
 * Mutable operation for setting MutableMixed object
 *
 * @param objectId - Object's id. Eg: collection's id, "items_display_preference", "items"
 * @param childMutableType - Mutable type of child object
 * @param childKey - Key to reference child's object
 * @param keyOrIndex - Key or index of child's property to mutate
 * @param value - New value to update child's property with
 * @param deltas - Delta of changes between old and new value
 * @param meta - Mutate's meta. { history: boolean, buffer: boolean }
 */
export const mixedSetOperation = (
    objectId: string,
    childMutableType: MutableType,
    childKey: string,
    keyOrIndex: string | number,
    value: Map<string, any>,
    deltas?: List<any>,
    meta = {},
) => {
    const operation = {
        childKey,
        key: childMutableType === MutableType.Map ? (keyOrIndex as string) : undefined,
        index: childMutableType === MutableType.List ? (keyOrIndex as number) : undefined,
        operation: OperationType.Set,
        deltas,
        value,
    };
    return mutateOperation(childMutableType, objectId, [operation], meta);
};

export const mixedInsertOperation = (
    objectId: string,
    childMutableType: MutableType,
    childKey: string,
    keyOrIndex: string | number,
    value: Map<string, any>,
    deltas?: List<any>,
    meta = {},
) => {
    const operation = {
        childKey,
        key: childMutableType === MutableType.Map ? (keyOrIndex as string) : undefined,
        index: childMutableType === MutableType.List ? (keyOrIndex as number) : undefined,
        operation: OperationType.Insert,
        deltas,
        value,
    };
    return mutateOperation(childMutableType, objectId, [operation], meta);
};

export const mixedRemoveOperation = (
    objectId: string,
    childMutableType: MutableType,
    childKey: string,
    keyOrIndex: string | number,
    meta = {},
) => {
    const operation = {
        childKey,
        key: childMutableType === MutableType.Map ? (keyOrIndex as string) : undefined,
        index: childMutableType === MutableType.List ? (keyOrIndex as number) : undefined,
        operation: OperationType.Remove,
    };
    return mutateOperation(childMutableType, objectId, [operation], meta);
};

interface IBulkMixedSetOperation {
    type: OperationType;
    childKey: string;
    key?: string;
    index?: number;
    value?: Map<string, any>;
    deltas?: List<any>;
}
export const bulkMixedSetOperation = (
    objectId: string,
    childMutableType: MutableType,
    operations: IBulkMixedSetOperation[],
    meta = {},
) => {
    const mutateOperations = operations.map((operation) => {
        return {
            childKey: operation.childKey,
            key: childMutableType === MutableType.Map ? operation.key : undefined,
            index: childMutableType === MutableType.List ? operation.index : undefined,
            operation: operation.type,
            deltas: operation.deltas,
            value: operation.value,
        };
    });
    return mutateOperation(childMutableType, objectId, mutateOperations, meta);
};

// GENERIC MUTATE OPERATIONS
export const mutateOperation = (
    mutableType: MutableType,
    objectId: string,
    operations: IOperation[],
    meta = {} as any,
) => {
    const { history = true, buffer = true, syncData } = meta;
    const action: any = {
        meta: {
            history,
            buffer,
        },
        payload: {
            mutableType,
            objectId,
            operations,
        },
        type: MUTATE_OPERATION,
    };

    if (syncData) {
        action.payload.syncData = syncData;
    }

    return action;
};

export const enhanceMutateOperation = (payload, meta = {}) => ({
    meta,
    payload,
    type: MUTATE_OPERATION_ENHANCED,
});

export const mapCreate = (objectType, id, value, ref) => {
    return {
        meta: {
            objectType,
            ref,
        },
        payload: { id, value },
        type: MUTATE_MAP_CREATE,
    };
};

export const listCreate = (objectType, ref, value) => {
    return {
        meta: {
            objectType,
            ref,
        },
        payload: { id: ref.object_id, value },
        type: MUTATE_LIST_CREATE,
    };
};

export const mixedCreate = (objectType, ref, value) => {
    return {
        meta: {
            objectType,
            ref,
        },
        payload: { id: ref.object_id, value },
        type: MUTATE_MIXED_CREATE,
    };
};

export const clear = (id) => {
    return {
        payload: { id },
        type: MUTATE_CLEAR,
    };
};

export interface IRetrySyncAction {
    type: string;
    payload: {
        objectNamespace: string;
        objectId: string;
    };
}

export interface IUncommittedMutationsSet {
    type: string;
    payload: {
        mutations: List<any>;
        objectId: string;
    };
}
